import {
  cloneElement,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@shared/lib/cn';

import {
  TOOLTIP_INLINE_POSITION_CLASS,
  TOOLTIP_OFFSET_PX,
  type TooltipPlacement,
  type TooltipTone,
} from './tooltip.constant';
import { TooltipBubble } from './tooltip-bubble.ui';

export interface TooltipProps {
  /** Figma `Text`(글자) 또는 `Content`(제목 + 여러 줄 등 내용). 글자 스타일은 caption */
  content: ReactNode;
  /** 기준 요소(마우스를 올리거나 포커스하면 뜬다). 없으면 말풍선만 그린다(inline 전용) */
  children?: ReactNode;
  /** Figma `tone`. light = 물음표 도움말(기준), dark = 차트 · 표. 기본 light */
  tone?: TooltipTone;
  /** Figma `placement`. 기준 요소의 어느 쪽에 뜨는지. 기본 top */
  placement?: TooltipPlacement;
  /** 제어 prop */
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** portal 대신 제자리에 그린다(fe-rules 3절). 확인 페이지는 inline 으로 늘어놓는다 */
  inline?: boolean;
  /** 말풍선에 붙는다 */
  className?: string;
  /**
   * 기준 요소를 감싸는 틀(`span`, 기본 inline-flex)에 붙는다. 말줄임하려면 틀이 줄어들어야 한다:
   * `triggerClassName="min-w-0 max-w-full"`(+ 안쪽 글자에 `truncate`)
   */
  triggerClassName?: string;
  /** 마우스를 올린 뒤(또는 포커스 뒤) 뜨기까지 기다리는 시간(ms). 닫힐 때는 기다리지 않는다. 기본 0 */
  delay?: number;
}

interface Position {
  top: number;
  left: number;
}

function place(trigger: DOMRect, bubble: DOMRect, placement: TooltipPlacement): Position {
  const gap = TOOLTIP_OFFSET_PX;
  switch (placement) {
    case 'top':
      return {
        top: trigger.top - bubble.height - gap,
        left: trigger.left + (trigger.width - bubble.width) / 2,
      };
    case 'bottom':
      return {
        top: trigger.bottom + gap,
        left: trigger.left + (trigger.width - bubble.width) / 2,
      };
    case 'left':
      return {
        top: trigger.top + (trigger.height - bubble.height) / 2,
        left: trigger.left - bubble.width - gap,
      };
    case 'right':
      return {
        top: trigger.top + (trigger.height - bubble.height) / 2,
        left: trigger.right + gap,
      };
  }
}

/** 기준 요소에 마우스를 올리거나 포커스하면 뜨는 짧은 설명 */
export function Tooltip({
  content,
  children,
  tone = 'light',
  placement = 'top',
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  inline = false,
  className,
  triggerClassName,
  delay = 0,
}: TooltipProps) {
  const id = useId();
  const [openState, setOpenState] = useState(defaultOpen);
  const open = openProp ?? openState;
  const setOpen = useCallback(
    (next: boolean) => {
      if (openProp === undefined) setOpenState(next);
      onOpenChange?.(next);
    },
    [openProp, onOpenChange],
  );

  // 뜨기 전 지연(delay). 닫으면 기다리던 것을 취소한다
  const delayTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearDelay = () => {
    if (delayTimer.current) clearTimeout(delayTimer.current);
    delayTimer.current = null;
  };
  useEffect(() => clearDelay, []);
  const show = () => {
    clearDelay();
    if (delay <= 0) {
      setOpen(true);
      return;
    }
    delayTimer.current = setTimeout(() => {
      delayTimer.current = null;
      setOpen(true);
    }, delay);
  };
  const hide = () => {
    clearDelay();
    setOpen(false);
  };

  const triggerRef = useRef<HTMLSpanElement>(null);
  const floatingRef = useRef<HTMLDivElement>(null);

  // portal 로 띄운 말풍선의 자리를 기준 요소에 맞춘다(재서 바로 DOM 에 적는다)
  useLayoutEffect(() => {
    const trigger = triggerRef.current;
    const floating = floatingRef.current;
    if (inline || !open || !trigger || !floating) return;
    const { top, left } = place(
      trigger.getBoundingClientRect(),
      floating.getBoundingClientRect(),
      placement,
    );
    floating.style.top = `${top}px`;
    floating.style.left = `${left}px`;
    floating.style.visibility = 'visible';
  }, [inline, open, placement, content]);

  const bubble = (extraClass?: string) => (
    <TooltipBubble
      id={id}
      tone={tone}
      placement={placement}
      content={content}
      className={cn(extraClass, className)}
    />
  );

  if (children === undefined) return open ? bubble() : null;

  const trigger = isValidElement(children)
    ? cloneElement(children as ReactElement<{ 'aria-describedby'?: string }>, {
        'aria-describedby': open ? id : undefined,
      })
    : children;

  return (
    <span
      ref={triggerRef}
      data-slot="tooltip-trigger"
      className={cn('inline-flex', inline && 'relative', triggerClassName)}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          clearDelay();
          if (open) setOpen(false);
        }
      }}
    >
      {trigger}
      {open && inline
        ? bubble(cn('absolute z-(--z-tooltip)', TOOLTIP_INLINE_POSITION_CLASS[placement]))
        : null}
      {open && !inline
        ? createPortal(
            <div
              ref={floatingRef}
              className="pointer-events-none invisible fixed top-0 left-0 z-(--z-tooltip)"
            >
              {bubble()}
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}
